import { memo, useEffect, useRef, useState } from 'react'
import {
  Box,
  CircularProgress,
  Divider,
  LinearProgress,
  Tooltip,
  Typography,
} from '@mui/material'
import { RotatingLines } from 'react-loader-spinner'
import { useApp, useAppActions, useNav } from '../AppContext'
import type { TabId, TabConfig } from '../types'
import SideNav from '../tabs/SideNav'
import ToastMessages from '../comps/ToastMessages'
import ModalHost from '../comps/ModalHost'
import taapi from '../services/taapi'
import { fetchSmInit } from '../services/TemplatesService'

import ResponseBuilderTab from '../tabs/ResponseBuilderTab'
import KnowledgeBaseTab from '../tabs/KnowledgeBaseTab'
import BidManagerTab from '../tabs/BidManagerTab'
import TaskManagerTab from '../tabs/TaskManagerTab'
import WordWizardTab from '../tabs/WordWizardTab'
import ScoreFixTab from '../tabs/ScoreFixTab'
import AdvancedEditTab from '../tabs/AdvancedEditTab'
// import QuickAskTab from '../components/tabs/QuickAskTab'
import QuickChatTab from '../tabs/ChatTab'
import TemplatesTab from '../tabs/TemplatesTab'
import SettingsTab from '../tabs/SettingsTab'
import iconKnowledge from '../img/knowledge_2640698.png'
import iconBidManager from '../img/data-processing_5864660.png'
import iconBuilder from '../img/blogger_950599.png'
import iconScoreFix from '../img/dashboard_2328909.png'
import iconWordWizard from '../img/crossword_6552376.png'
import iconAdvEdit from '../img/pencil_1639176.png'
import iconQuickChat from '../img/chat_427936.png'
import iconTasks from '../img/checkup_2644437.png'
import iconTemplates from '../img/paper-note_1646066.png'
import iconSettings from '../img/building_1740341.png'
import iconAutoFix from '../img/autofixhigh.png'

import { COLOR_PRIMARY } from '../common/theme'

export const TABS: TabConfig[] = [
  { id: 'knowledge', label: 'Knowledge Base', icon: iconKnowledge },
  { id: 'projectstab', label: 'Bid Manager', icon: iconBidManager },
  { id: 'response', label: 'Builder', title: 'Response Builder', icon: iconBuilder, groupStart: 'Response', grouped: true },
  { id: 'bideval', label: 'Score & Fix', icon: iconScoreFix, grouped: true },
  { id: 'wordcount', label: 'Word Wizard', icon: iconAutoFix, grouped: true },
  { id: 'advedit', label: 'Interactive Edit', icon: iconAdvEdit, grouped: true },
  { id: 'quickchat', label: 'Chat', icon: iconQuickChat },
  { id: 'tasks', label: 'Tasks', icon: iconTasks },
  { id: 'styletab', label: 'Templates', icon: iconTemplates },
  { id: 'settings', label: 'Settings', icon: iconSettings },
]

export const LITE_TABS = new Set<TabId>(['knowledge', 'quickchat', 'tasks', 'settings'])


// Tabs that stack content vertically
const COLUMN_TABS = new Set<TabId>([
  'knowledge', 'projectstab', 'tasks', 'wordcount', 'bideval', 'advedit', 'quickchat', 'styletab', 'settings',
])

// Memoized so that tab switches (NavContext change) don't re-render mounted tabs
const MemoResponseBuilderTab = memo(ResponseBuilderTab)
const MemoKnowledgeBaseTab = memo(KnowledgeBaseTab)
const MemoBidManagerTab = memo(BidManagerTab)
const MemoTaskManagerTab = memo(TaskManagerTab)
const MemoWordWizardTab = memo(WordWizardTab)
const MemoScoreFixTab = memo(ScoreFixTab)
const MemoAdvancedEditTab = memo(AdvancedEditTab)
const MemoQuickChatTab = memo(QuickChatTab)
const MemoTemplatesTab = memo(TemplatesTab)
const MemoSettingsTab = memo(SettingsTab)

export default function AppPage() {
  const { activeTab } = useNav()
  const { isLoading, isProcessing, processingMessage, tabLoading, taskCount, activeTasks } = useApp()
  const { setIsLoading, setAcInit, setStyleNames, setElementGroups, kickTaskstream } = useAppActions()

  const [initError, setInitError] = useState<string | null>(null)

  // Keep tabs mounted after their first visit so state and API calls are preserved
  const mountedRef = useRef<Set<TabId>>(new Set([activeTab]))
  if (!mountedRef.current.has(activeTab)) mountedRef.current.add(activeTab)
  const mounted = mountedRef.current

  // Kick the taskstream once we're inside the authenticated app shell.
  // AppProvider's initial connect bails when there's no auth cookie (so the
  // login page doesn't open a doomed SSE socket); this is the matching
  // signal that auth is now present and the stream should open.
  useEffect(() => {
    kickTaskstream()
  }, [kickTaskstream])

  useEffect(() => {
    let cancelled = false
    setIsLoading(true)
    Promise.all([
      taapi.get('/api2/acinit'),
      fetchSmInit(),
    ])
      .then(([acData, smData]: [Record<string, any>, any]) => {
        if (cancelled) return
        if (acData) setAcInit(acData)
        if (smData?.stylegroups) setStyleNames((smData.stylegroups as any[]).map(g => g.name as string))
        if (smData?.elementgroups) setElementGroups(smData.elementgroups as any[])
      })
      .catch(() => { if (!cancelled) setInitError('Failed to load app data. Please refresh.') })
      .finally(() => { if (!cancelled) setIsLoading(false) })
    return () => { cancelled = true }
  }, [])

  const tabStyle = (id: TabId) =>
    activeTab === id
      ? { display: 'flex' as const, flexDirection: COLUMN_TABS.has(id) ? 'column' as const : 'row' as const }
      : { display: 'none' as const }

  return (
    <Box sx={{ height: '100%', display: 'flex', overflow: 'hidden' }}>
      <SideNav />
      <Divider orientation="vertical" flexItem />

      {/* Main area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>

        {/* Page header */}
        <Box
          component="header"
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            flexShrink: 0,
            minHeight: 48,
            borderBottom: 1,
            borderColor: 'divider',
            position: 'relative',
          }}
        >
          {/* Active tab title */}
          {TABS.map(h => (
            <Typography
              key={h.id}
              variant="h6"
              sx={{
                display: activeTab === h.id ? 'block' : 'none',
                fontSize: '0.95rem',
                fontWeight: 600,
              }}
            >
              {h.title ?? h.label}
            </Typography>
          ))}

          {/* Processing message */}
          {processingMessage && (
            <Typography
              variant="caption"
              sx={{ position: 'absolute', right: isProcessing ? 40 : 16, opacity: 0.65 }}
            >
              {processingMessage}
            </Typography>
          )}

          {/* Processing spinner */}
          {isProcessing && (() => {
            const counts = new Map<string, number>()
            for (const t of activeTasks) {
              counts.set(t.tasktypename, (counts.get(t.tasktypename) ?? 0) + 1)
            }
            const parts = Array.from(counts.entries()).map(([name, n]) => n > 1 ? `${name} (${n})` : name)
            const summary = parts.length > 0 ? parts.join(', ') : `${taskCount} Task${taskCount === 1 ? '' : 's'}`
            const tooltipTitle = parts.length > 0
              ? `Our servers are processing: ${parts.join(', ')}`
              : 'Our servers are processing your task'
            return (
              <Tooltip title={tooltipTitle} placement="left">
                <Box sx={{ position: 'absolute', right: 14, display: 'flex', alignItems: 'center', gap: 1, opacity: 0.65 }}>
                  <Typography
                    variant="caption"
                    sx={{ fontSize: '0.72rem', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {summary}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', opacity: 0.75 }}>
                    <RotatingLines strokeColor={COLOR_PRIMARY} strokeWidth="5" animationDuration="1" width="30" visible />
                  </Box>
                </Box>
              </Tooltip>
            )
          })()}

          {/* Tab-level loading bar */}
          {tabLoading && (
            <LinearProgress
              sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2 }}
            />
          )}
        </Box>

        {/* Content area */}
        {isLoading ? (
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <CircularProgress />
          </Box>
        ) : initError ? (
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Typography color="error">{initError}</Typography>
          </Box>
        ) : (
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box style={tabStyle('response')} sx={{ flex: 1, minHeight: 0 }}>{mounted.has('response') && <MemoResponseBuilderTab />}</Box>
              <Box style={tabStyle('knowledge')} sx={{ flex: 1, minHeight: 0 }}>{mounted.has('knowledge') && <MemoKnowledgeBaseTab />}</Box>
              <Box style={tabStyle('projectstab')} sx={{ flex: 1, minHeight: 0 }}>{mounted.has('projectstab') && <MemoBidManagerTab />}</Box>
              <Box style={tabStyle('tasks')} sx={{ flex: 1, minHeight: 0 }}>{mounted.has('tasks') && <MemoTaskManagerTab />}</Box>
              <Box style={tabStyle('wordcount')} sx={{ flex: 1, minHeight: 0 }}>{mounted.has('wordcount') && <MemoWordWizardTab />}</Box>
              <Box style={tabStyle('bideval')} sx={{ flex: 1, minHeight: 0 }}>{mounted.has('bideval') && <MemoScoreFixTab />}</Box>
              <Box style={tabStyle('advedit')} sx={{ flex: 1, minHeight: 0 }}>{mounted.has('advedit') && <MemoAdvancedEditTab />}</Box>
              {/* <Box style={tabStyle('chat')}     sx={{ flex: 1, minHeight: 0 }}>{mounted.has('chat')        && <MemoQuickAskTab />}</Box> */}
              <Box style={tabStyle('quickchat')} sx={{ flex: 1, minHeight: 0 }}>{mounted.has('quickchat') && <MemoQuickChatTab />}</Box>
              <Box style={tabStyle('styletab')} sx={{ flex: 1, minHeight: 0 }}>{mounted.has('styletab') && <MemoTemplatesTab />}</Box>
              <Box style={tabStyle('settings')} sx={{ flex: 1, minHeight: 0 }}>{mounted.has('settings') && <MemoSettingsTab />}</Box>
            </Box>
          </Box>
        )}

        <ToastMessages />
        <ModalHost />
      </Box>
    </Box>
  )
}
